import { Link } from "react-router-dom";
import type { Leak } from "@/mocks/leaks";
import { formatHot, getCategoryName } from "@/lib/leaks";

interface LeakCardProps {
  leak: Leak;
  variant?: "default" | "wide" | "compact";
  showSummary?: boolean;
}

export default function LeakCard({
  leak,
  variant = "default",
  showSummary = true,
}: LeakCardProps) {
  const categoryName = getCategoryName(leak.category);
  const href = `/leak/${leak.id}`;

  if (variant === "compact") {
    return (
      <article className="border-b border-background-200 py-4 last:border-b-0">
        <Link to={href} className="group flex items-start gap-4">
          <span className="mt-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-primary-50 text-xs font-bold text-primary-600">
            {leak.hot >= 8000 ? "热" : "新"}
          </span>
          <span className="flex-1">
            <h3 className="font-heading text-base font-semibold leading-snug text-foreground-950 transition-colors group-hover:text-primary-600">
              {leak.title}
            </h3>
            <p className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-foreground-500">
              <span className="rounded bg-secondary-100 px-1.5 py-0.5 text-secondary-800">{categoryName}</span>
              <span>{leak.publishedAt}</span>
              <span className="flex items-center gap-1 text-primary-600">
                <i className="ri-fire-line" />
                {formatHot(leak.hot)}
              </span>
            </p>
          </span>
        </Link>
      </article>
    );
  }

  if (variant === "wide") {
    return (
      <article className="overflow-hidden rounded-lg border border-background-200 bg-background-100 transition-colors hover:border-primary-300">
        <Link to={href} className="group grid grid-cols-1 md:grid-cols-2">
          <div className="relative h-52 w-full md:h-full md:min-h-[260px]">
            <img
              src={leak.cover}
              alt={`${leak.title} — 蝌蚪爆料现场画面`}
              title={`${leak.title} 蝌蚪爆料`}
              className="h-full w-full object-cover object-top"
            />
            <span className="absolute left-3 top-3 rounded-md bg-primary-500 px-2 py-1 text-xs font-medium text-background-50">
              {categoryName}
            </span>
          </div>
          <div className="flex flex-col justify-center p-5 md:p-7">
            <h3 className="font-heading text-xl font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-600 md:text-2xl">
              {leak.title}
            </h3>
            {showSummary ? (
              <p className="mt-3 line-clamp-3 text-sm leading-relaxed text-foreground-600">
                {leak.summary}
              </p>
            ) : null}
            <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-foreground-500">
              <span className="flex items-center gap-1.5">
                <i className="ri-map-pin-line" />
                {leak.source}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line" />
                {leak.publishedAt}
              </span>
              <span className="flex items-center gap-1.5 text-primary-600">
                <i className="ri-fire-line" />
                {formatHot(leak.hot)} 热度
              </span>
            </div>
          </div>
        </Link>
      </article>
    );
  }

  return (
    <article
     
      className="group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-100 transition-colors hover:border-primary-300"
    >
      <Link to={href} className="flex h-full flex-col">
        <div className="relative h-44 w-full overflow-hidden">
          <img
            src={leak.cover}
            alt={`${leak.title} — 蝌蚪爆料现场画面`}
            title={`${leak.title} 蝌蚪爆料`}
            className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.04]"
          />
          <span className="absolute left-3 top-3 rounded-md bg-background-50/95 px-2 py-1 text-xs font-medium text-primary-700">
            {categoryName}
          </span>
        </div>
        <div className="flex flex-1 flex-col p-4">
          <h3 className="font-heading text-base font-semibold leading-snug text-foreground-950 transition-colors group-hover:text-primary-600">
            {leak.title}
          </h3>
          {showSummary ? (
            <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-600">
              {leak.summary}
            </p>
          ) : null}
          <div className="mt-auto flex flex-wrap items-center gap-x-3 gap-y-1 pt-4 text-xs text-foreground-500">
            <span className="flex items-center gap-1">
              <i className="ri-time-line" />
              {leak.publishedAt.slice(5, 10)}
            </span>
            <span className="flex items-center gap-1 text-primary-600">
              <i className="ri-fire-line" />
              {formatHot(leak.hot)}
            </span>
            <span className="ml-auto flex items-center gap-1">
              <i className="ri-book-open-line" />
              {leak.readingTime}
            </span>
          </div>
        </div>
      </Link>
    </article>
  );
}